import { useDownloadPdf } from '@kanda-libs/ks-component-ts';
import {
  type Company,
  type InfoWorkType,
  type Job,
  type JobCreditState,
} from '@kanda-libs/ks-frontend-services';
import { useCurrentCompany } from 'hooks';
import useRefreshingData from 'hooks/useRefreshingData';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FunctionComponent,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';
import { checkJobChangesRequested } from 'utils/Job-functions';

export interface JobContextType {
  job: Job | undefined;
  checkJob: JobCreditState | undefined;
  company: Company | undefined;
  infoWorkTypes: InfoWorkType[] | undefined;
  archived: boolean;
  example: boolean;
  id: string | undefined;
  changesRequested: boolean;
  downloadPdf: (id: string) => void;
  status: NonNullable<Job['status']>;
  isLoading: boolean;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
}

export const JobContext = createContext<JobContextType>({
  job: undefined,
  checkJob: undefined,
  company: undefined,
  infoWorkTypes: undefined,
  archived: false,
  example: false,
  id: undefined,
  changesRequested: false,
  downloadPdf: () => {},
  status: 'draft',
  isLoading: false,
  isSubmitting: false,
  setIsSubmitting: () => {},
});

export const useJobContext = () => useContext(JobContext);

export interface JobProviderProps {
  children: ReactNode;
}

const JobProvider: FunctionComponent<JobProviderProps> = function ({
  children,
}) {
  const { cleanUp } = useRefreshingData(20000);

  const [contextIsSubmitting, setIsSubmitting] = useState<boolean>(false);

  const job = useSelector(selectors.job.getEntity);
  const jobHasFetched = useSelector(selectors.job.getHasFetched);
  const jobIsSubmitting = useSelector(selectors.job.getIsSubmitting);

  const id = useSelector(selectors.job.getId);

  const checkJob = useSelector(selectors.checkJob.getEntity);
  const checkJobHasFetched = useSelector(selectors.checkJob.getHasFetched);

  const infoWorkTypes = useSelector(selectors.infoWorkType.getRawResponse) as
    | InfoWorkType[]
    | undefined;

  const { company } = useCurrentCompany();
  const { downloadPdf, isSubmitting: pdfDownloadIsSubmitting } =
    useDownloadPdf();

  const archived = useMemo(() => {
    if (!job) return false;
    return job.archived === 'yes';
  }, [job]);

  const example = useMemo(() => {
    if (!job) return false;
    return job.flow_type === 'example';
  }, [job]);

  const status = useMemo(() => {
    if (!job) return 'draft';
    return job.status || 'draft';
  }, [job]);

  const changesRequested = useMemo(() => {
    if (!job) return false;
    return checkJobChangesRequested(job);
  }, [job]);

  const isLoading = useMemo(
    () => !jobHasFetched || !company || !checkJobHasFetched,
    [jobHasFetched, company, checkJobHasFetched],
  );

  const isSubmitting = useMemo(
    () => pdfDownloadIsSubmitting || contextIsSubmitting || jobIsSubmitting,
    [pdfDownloadIsSubmitting, contextIsSubmitting, jobIsSubmitting],
  );

  const values = useMemo(
    () => ({
      job,
      checkJob,
      company: company || undefined,
      infoWorkTypes,
      archived,
      example,
      id,
      changesRequested,
      downloadPdf,
      status,
      isLoading,
      isSubmitting,
      setIsSubmitting,
    }),
    [
      job,
      checkJob,
      company,
      infoWorkTypes,
      archived,
      example,
      id,
      changesRequested,
      downloadPdf,
      status,
      isLoading,
      isSubmitting,
    ],
  );

  useEffect(() => () => cleanUp(), [cleanUp]);

  return <JobContext.Provider value={values}>{children}</JobContext.Provider>;
};

export default JobProvider;
